/**
 * What happens to undo material once the design has been exported.
 *
 * The export is the deliverable. After it, per-step and per-tool history stops
 * earning its storage: nobody re-opens a shipped order to nudge a flare by half a
 * millimetre, and if they do, the right unit of work is a whole stage, not a
 * single tool. So the order collapses to **one retained mesh per design stage**
 * and everything else is deleted — locally and on the server.
 *
 * The consequence for navigation is deliberate and visible: "Previous" can no
 * longer land mid-stage, because there is nothing left to reconstruct mid-stage
 * from. It lands on the first step of the previous stage instead. See
 * `resolvePrunedPreviousStep`.
 *
 * The exported flag is kept in localStorage rather than in a store, because
 * navigation has to answer "is this order pruned?" synchronously during a click
 * handler, on the very first render after a reload, before any store has hydrated.
 */
import { loadBkDesignOrderContext } from '../../../shared/lib/bkDesignOrderContext';
import {
  RETAINED_STEPS_AFTER_EXPORT,
  resolvePrunedPreviousStep,
} from '../../../shared/constants/designStages';
import { pruneUndoMaterialToStages } from './undoInvalidation';
import { clearAllUndoSnapshots } from '../undoSnapshot';
import useOperationLogStore from '../operationLog/operationLogStore';

const LOG_PREFIX = '[PostExport]';
const STORE_KEY = 'bk_design_exported_orders_v1';

function readExported() {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeExported(map) {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(map));
  } catch (e) {
    console.warn(`${LOG_PREFIX} could not persist exported flag`, e);
  }
}

function currentOrderKey() {
  const ctx = loadBkDesignOrderContext();
  return ctx?.sales_order && ctx?.customer ? `${ctx.sales_order}:${ctx.customer}` : null;
}

/**
 * Has this order been exported (and therefore pruned to stage granularity)?
 * Synchronous by design — see the note above.
 *
 * @param {string} [orderKey] defaults to the open order
 */
export function isDesignExported(orderKey = currentOrderKey()) {
  if (!orderKey) return false;
  return !!readExported()[orderKey];
}

/** @param {string} [orderKey] */
export function markDesignExported(orderKey = currentOrderKey()) {
  if (!orderKey) return;
  const map = readExported();
  map[orderKey] = { at: Date.now(), retained: RETAINED_STEPS_AFTER_EXPORT };
  writeExported(map);
}

/**
 * Undo the exported flag — the order was reopened for further work, so per-step
 * history starts accumulating again from here. The already-pruned material does
 * not come back; only newly applied tools get before-meshes.
 *
 * @param {string} [orderKey]
 */
export function clearDesignExported(orderKey = currentOrderKey()) {
  if (!orderKey) return;
  const map = readExported();
  if (!map[orderKey]) return;
  delete map[orderKey];
  writeExported(map);
}

/**
 * Run the prune. Called after the final STL upload succeeds, before the redirect.
 *
 * Never throws: the export itself has already succeeded at this point, and a
 * failed cleanup must not turn a successful export into an error the user sees.
 *
 * @returns {Promise<{ ok: boolean, localDeleted?: number, retained?: string[] }>}
 */
export async function runPostExportPrune() {
  const orderKey = currentOrderKey();
  try {
    // In-session pools go first — they are the largest consumer of memory and are
    // meaningless now that per-tool undo is gone.
    clearAllUndoSnapshots();
    const opLog = useOperationLogStore.getState();
    for (const step of Object.keys(opLog.operationsByStep)) {
      if (!RETAINED_STEPS_AFTER_EXPORT.includes(step)) {
        opLog.clearOperationsForStep(step);
      }
    }

    const result = await pruneUndoMaterialToStages();
    markDesignExported(orderKey);
    console.log(
      `${LOG_PREFIX} pruned to stage granularity — retained ${result.retained.join(', ')}`,
    );
    return { ok: true, ...result };
  } catch (e) {
    console.warn(`${LOG_PREFIX} prune failed (export itself succeeded)`, e);
    // Still mark it: navigation must behave as pruned, because the *intent* is
    // that per-step material is gone. Anything the delete missed is unreachable
    // anyway — hydration is gated on completion state, not file presence.
    markDesignExported(orderKey);
    return { ok: false };
  }
}

/**
 * Clamp a backward-navigation target to what still exists.
 *
 * Before export this is the identity function. After it, any target that is not a
 * retained stage checkpoint is pulled back to the start of its stage.
 *
 * @param {string} currentStep
 * @param {string} requestedTarget
 * @returns {{ target: string, clamped: boolean }}
 */
export function clampBackwardTarget(currentStep, requestedTarget) {
  if (!isDesignExported()) return { target: requestedTarget, clamped: false };

  const pruned = resolvePrunedPreviousStep(currentStep);
  if (!pruned || pruned === requestedTarget) {
    return { target: requestedTarget, clamped: false };
  }
  console.log(
    `${LOG_PREFIX} ${requestedTarget} is no longer reconstructable — going back to ${pruned}`,
  );
  return { target: pruned, clamped: true };
}

/** Message for the confirm dialog when a target has been clamped to a stage start. */
export function stageRewindMessage(target) {
  return (
    'This design has been exported, so only one save point per stage is kept. ' +
    `Going back will restart from ${String(target).toLowerCase().replace(/_/g, ' ')} ` +
    'and discard everything after it.'
  );
}
